import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Form, Row, InputGroup } from 'react-bootstrap';
import Button from '../../components/base/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as MuiButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useCreateUserMutation } from '../authentication/data-access/+state/user.api';
import { buildCreateAccountPayload, CREATE_ACCOUNT_DOMAIN_OPTIONS } from '@shared/types/user.types';
import { useGetDivisionsQuery } from '../divisions/divisions.api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faKey } from '@fortawesome/free-solid-svg-icons';

const AddUser = () => {
  const navigate = useNavigate();
  const [createUser, { isLoading, isSuccess }] = useCreateUserMutation();

  // Get organizationId from localStorage
  const organizationId = localStorage.getItem('organizationId') || '';

  const { data: divisionsData, isLoading: divisionsLoading } = useGetDivisionsQuery(organizationId, {
    skip: !organizationId // Skip if no organizationId
  });

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [serverError, setServerError] = useState('');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [role, setRole] = useState('');
  const [domain, setDomain] = useState('healthcare');
  const [fieldError, setFieldError] = useState<{ [key: string]: string }>({});
  const [useGeneratedPassword, setUseGeneratedPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const generatePassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let generatedPassword = '';
    for (let i = 0; i < length; i++) {
      generatedPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return generatedPassword;
  };

  const handleGeneratePassword = () => {
    const generatedPass = generatePassword();
    setPassword(generatedPass);
    setConfirmPassword(generatedPass);
    setUseGeneratedPassword(true);
    setShowPassword(true);
  };

  const handleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleAddUser = async () => {
    setFieldError({});
    setServerError('');

    if (!username) {
      setFieldError(prev => ({ ...prev, username: 'Email is required' }));
      return;
    }

    if (!isValidEmail(username)) {
      setFieldError(prev => ({
        ...prev,
        username: 'Please enter a valid email'
      }));
      return;
    }

    if (!firstName) {
      setFieldError(prev => ({ ...prev, firstName: 'First name is required' }));
      return;
    }

    if (!lastName) {
      setFieldError(prev => ({ ...prev, lastName: 'Last name is required' }));
      return;
    }

    if (!domain) {
      setFieldError(prev => ({ ...prev, domain: 'Domain is required' }));
      return;
    }

    if (!password) {
      setFieldError(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }

    if (!useGeneratedPassword && password !== confirmPassword) {
      setFieldError(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
      return;
    }

    try {
      const response = await createUser(
        buildCreateAccountPayload({
          username,
          password,
          organizationName: '',
          organizationId: organizationId || undefined,
          firstName,
          lastName,
          domain,
          divisionId,
          role
        })
      ).unwrap();

      // Store organizationId from response if not already stored
      if (response?.organizationId && !organizationId) {
        localStorage.setItem('organizationId', response.organizationId);
      }
    } catch (error: any) {
      if (error?.data?.message) {
        setServerError(error.data.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
      setShowErrorSnackbar(true);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setShowSuccessDialog(true);
    }
  }, [isSuccess]);

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/users/view-all'); // Navigate to view all users
  };

  return (
    <div className="px-4 px-lg-6 pt-6 pb-9">
      {/* <PageBreadcrumb items={defaultBreadcrumbItems} /> */}

      <div className="mb-4">
        <h2>Add User</h2>
        <p className="text-muted">Create a new user account</p>
      </div>

      <Row>
        <Col xs={12} lg={8} xl={6}>
          <Form>
            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group className="text-start">
                  <Form.Label htmlFor="firstName">First Name</Form.Label>
                  <Form.Control
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={e => {
                      setFirstName(e.target.value);
                      setFieldError(prev => ({ ...prev, firstName: '' }));
                    }}
                    placeholder="John"
                    isInvalid={!!fieldError.firstName}
                  />
                  <Form.Control.Feedback type="invalid">{fieldError.firstName}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col sm={6}>
                <Form.Group className="text-start">
                  <Form.Label htmlFor="lastName">Last Name</Form.Label>
                  <Form.Control
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={e => {
                      setLastName(e.target.value);
                      setFieldError(prev => ({ ...prev, lastName: '' }));
                    }}
                    placeholder="Doe"
                    isInvalid={!!fieldError.lastName}
                  />
                  <Form.Control.Feedback type="invalid">{fieldError.lastName}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3 text-start">
              <Form.Label htmlFor="email">Email address</Form.Label>
              <Form.Control
                id="email"
                type="email"
                value={username}
                onChange={e => {
                  setUsername(e.target.value);
                  setFieldError(prev => ({ ...prev, username: '' }));
                }}
                placeholder="name@example.com"
                isInvalid={!!fieldError.username}
              />
              <Form.Control.Feedback type="invalid">{fieldError.username}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3 text-start">
              <Form.Label htmlFor="domain">Domain</Form.Label>
              <Form.Select
                id="domain"
                value={domain}
                onChange={e => {
                  setDomain(e.target.value);
                  setFieldError(prev => ({ ...prev, domain: '' }));
                }}
                isInvalid={!!fieldError.domain}
              >
                {CREATE_ACCOUNT_DOMAIN_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{fieldError.domain}</Form.Control.Feedback>
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col sm={6}>
                <Form.Group className="text-start">
                  <Form.Label htmlFor="divisionId">Division</Form.Label>
                  <Form.Select
                    id="divisionId"
                    value={divisionId}
                    onChange={e => {
                      setDivisionId(e.target.value);
                      setFieldError(prev => ({ ...prev, divisionId: '' }));
                    }}
                    isInvalid={!!fieldError.divisionId}
                    disabled={divisionsLoading}
                  >
                    <option value="">Select Division</option>
                    {(divisionsData?.divisions || [])?.map(division => (
                      <option key={division._id} value={division._id}>
                        {division.title}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{fieldError.divisionId}</Form.Control.Feedback>
                  {divisionsLoading && <small className="text-muted">Loading divisions...</small>}
                </Form.Group>
              </Col>

              <Col sm={6}>
                <Form.Group className="text-start">
                  <Form.Label htmlFor="role">Role</Form.Label>
                  <Form.Select
                    id="role"
                    value={role}
                    onChange={e => {
                      setRole(e.target.value);
                      setFieldError(prev => ({ ...prev, role: '' }));
                    }}
                    isInvalid={!!fieldError.role}
                  >
                    <option value="">Select Role</option>
                    <option value="Admin">Admin</option>
                    <option value="Developer">Developer</option>
                    <option value="Reviewer">Reviewer</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{fieldError.role}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-3">
              <div className="d-flex gap-2 mb-2">
                <Button
                  variant={useGeneratedPassword ? 'phoenix-primary' : 'phoenix-secondary'}
                  onClick={handleGeneratePassword}
                  className="flex-fill"
                >
                  <FontAwesomeIcon icon={faKey} className="me-2" />
                  Generate Password
                </Button>
                {useGeneratedPassword && (
                  <Button
                    variant="phoenix-secondary"
                    onClick={() => {
                      setUseGeneratedPassword(false);
                      setPassword('');
                      setConfirmPassword('');
                      setShowPassword(false);
                    }}
                  >
                    Use Custom Password
                  </Button>
                )}
              </div>
            </div>

            {!useGeneratedPassword ? (
              <>
                <Form.Group className="mb-3 text-start">
                  <Form.Label htmlFor="password">Password</Form.Label>
                  <Form.Control
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => {
                      setPassword(e.target.value);
                      setFieldError(prev => ({ ...prev, password: '' }));
                    }}
                    placeholder="Password"
                    isInvalid={!!fieldError.password}
                  />
                  <Form.Control.Feedback type="invalid">{fieldError.password}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3 text-start">
                  <Form.Label htmlFor="confirmPassword">Confirm Password</Form.Label>
                  <Form.Control
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => {
                      setConfirmPassword(e.target.value);
                      setFieldError(prev => ({ ...prev, confirmPassword: '' }));
                    }}
                    placeholder="Confirm Password"
                    isInvalid={!!fieldError.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">{fieldError.confirmPassword}</Form.Control.Feedback>
                </Form.Group>
              </>
            ) : (
              <Form.Group className="mb-3 text-start">
                <Form.Label>Generated Password</Form.Label>
                <InputGroup>
                  <Form.Control type={showPassword ? 'text' : 'password'} value={password} readOnly />
                  <Button variant="phoenix-secondary" onClick={handleShowPassword}>
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
                <small className="text-muted">
                  This password will be sent to the user via email. Please share it securely.
                </small>
              </Form.Group>
            )}

            {serverError && <div className="text-danger text-center mb-3">{serverError}</div>}

            <div className="d-flex gap-2">
              <Button variant="phoenix-primary" onClick={handleAddUser} disabled={isLoading}>
                {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Create User'}
              </Button>
              <Button variant="phoenix-secondary" onClick={() => navigate('/users/view-all')}>
                Cancel
              </Button>
            </div>
          </Form>
        </Col>
      </Row>

      {/* Success Modal */}
      <Dialog open={showSuccessDialog} onClose={handleDialogClose}>
        <DialogTitle>User Created</DialogTitle>
        <DialogContent>
          <DialogContentText>The user account has been created successfully.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleDialogClose} autoFocus>
            Okay
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" variant="filled">
          {serverError}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddUser;
